import { useState, useCallback } from 'react';

interface ApiState<T> {
    data: T | null;
    isLoading: boolean;
    error: string | null;
}

interface UseApiOptions {
    onSuccess?: (data: unknown) => void;
    onError?: (error: string) => void;
}

export function useApi<T = unknown>(options: UseApiOptions = {}) {
    const [state, setState] = useState<ApiState<T>>({
        data: null,
        isLoading: false,
        error: null,
    });

    const request = useCallback(
        async (url: string, init?: RequestInit): Promise<T | null> => {
            setState((prev) => ({ ...prev, isLoading: true, error: null }));

            try {
                const response = await fetch(url, {
                    ...init,
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        ...init?.headers,
                    },
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || 'Request failed');
                }

                setState({ data: data.data ?? data, isLoading: false, error: null });
                options.onSuccess?.(data);
                return data.data ?? data;
            } catch (error) {
                const message = error instanceof Error ? error.message : 'Unknown error';
                setState((prev) => ({ ...prev, isLoading: false, error: message }));
                options.onError?.(message);
                return null;
            }
        },
        [options]
    );

    const get = useCallback(
        (url: string) => request(url, { method: 'GET' }),
        [request]
    );

    const post = useCallback(
        (url: string, body?: unknown) =>
            request(url, { method: 'POST', body: JSON.stringify(body) }),
        [request]
    );

    const put = useCallback(
        (url: string, body?: unknown) =>
            request(url, { method: 'PUT', body: JSON.stringify(body) }),
        [request]
    );

    const del = useCallback(
        (url: string) => request(url, { method: 'DELETE' }),
        [request]
    );

    return {
        ...state,
        get,
        post,
        put,
        del,
        request,
    };
}

// Simple fetch wrapper for one-off requests
export async function api<T = unknown>(
    url: string,
    init?: RequestInit
): Promise<{ data: T | null; error: string | null }> {
    try {
        const response = await fetch(url, {
            ...init,
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                ...init?.headers,
            },
        });

        const data = await response.json();

        if (!response.ok) {
            return { data: null, error: data.error || 'Request failed' };
        }

        return { data: data.data ?? data, error: null };
    } catch (error) {
        return {
            data: null,
            error: error instanceof Error ? error.message : 'Unknown error',
        };
    }
}
